import { useCallback, useMemo } from "react"

import {
  constraintsFromSkuSettings,
  indexVariantViolations,
  partiallySelectedShortGroups,
  resolveColli,
  resolveMoq,
  type QuantityCell,
  type SkuConstraint,
  type VariantViolationIndex,
} from "~/utils/quantityViolations"
import { filterDefined } from "~/utils/tsUtils"

/** Runs a resolution over the caller's cells; the caller writes back what changed. */
export type ViolationResolver = <T extends QuantityCell>(cells: readonly T[]) => T[]

type UseQuantityViolationsArgs = {
  /** Every SKU × warehouse cell the rules see, zeros included. */
  cells: readonly QuantityCell[]
  constraints: readonly SkuConstraint[] | undefined
  variantIdBySku: ReadonlyMap<string, string>
  /** A warehouse filter locks MOQ resolution and scopes colli resolution and its count. */
  selectedWarehouseId?: string | null
  /** Warehouses decided elsewhere (synced or pre-orders-only): count towards MOQ, never colli, never resolved into. */
  lockedWarehouseIds?: ReadonlySet<string>
  /** Why MOQ resolution is unavailable for a table-specific reason; wins over the warehouse filter. */
  extraMoqLock?: string
  isCellLocked?: (skuId: string, warehouseId: string) => boolean
  applyChanges: (resolve: ViolationResolver) => void
}

const NO_CONSTRAINTS: readonly SkuConstraint[] = []

const WAREHOUSE_FILTER_MOQ_LOCK =
  "MOQ is measured across every warehouse, so resolving it is unavailable while the view is filtered to one. Clear the warehouse filter to resolve."

/** MOQ/colli detection, badge counts and resolution for a quantity table (SUP-408). */
export function useQuantityViolations({
  cells,
  constraints = NO_CONSTRAINTS,
  variantIdBySku,
  selectedWarehouseId,
  lockedWarehouseIds,
  extraMoqLock,
  isCellLocked,
  applyChanges,
}: UseQuantityViolationsArgs) {
  const index = useMemo<VariantViolationIndex>(
    () =>
      indexVariantViolations(cells, constraints, variantIdBySku, {
        colliExemptWarehouseIds: lockedWarehouseIds,
      }),
    [cells, constraints, variantIdBySku, lockedWarehouseIds],
  )

  // Counts what the colli button will fix: the filtered warehouse only, when filtered.
  const colliCount = useMemo(() => {
    if (!selectedWarehouseId) return index.colliVariantIds.size
    const variantIds = new Set<string>()
    for (const [skuId, perWarehouse] of index.colliBySku) {
      const variantId = variantIdBySku.get(skuId)
      if (variantId && perWarehouse.has(selectedWarehouseId)) variantIds.add(variantId)
    }
    return variantIds.size
  }, [index, selectedWarehouseId, variantIdBySku])

  const moqLockedReason =
    extraMoqLock ?? (selectedWarehouseId ? WAREHOUSE_FILTER_MOQ_LOCK : undefined)

  const partialGroups = useCallback(
    (selectedSkuIds: ReadonlySet<string>) =>
      selectedSkuIds.size > 0
        ? partiallySelectedShortGroups(cells, constraints, selectedSkuIds)
        : [],
    [cells, constraints],
  )

  const resolveMoqViolations = (selectedSkuIds?: ReadonlySet<string>) => {
    if (moqLockedReason || constraints.length === 0) return
    applyChanges((current) =>
      resolveMoq(current, constraints, {
        selectedSkuIds,
        lockedWarehouseIds,
        isCellLocked,
      }),
    )
  }

  const resolveColliViolations = (selectedSkuIds?: ReadonlySet<string>) => {
    if (constraints.length === 0) return
    applyChanges((current) =>
      resolveColli(current, constraints, {
        selectedSkuIds,
        colliExemptWarehouseIds: lockedWarehouseIds,
        isCellLocked: (skuId, warehouseId) =>
          !!isCellLocked?.(skuId, warehouseId) ||
          (!!selectedWarehouseId && warehouseId !== selectedWarehouseId),
      }),
    )
  }

  return {
    index,
    moqCount: index.moqVariantIds.size,
    colliCount,
    moqLockedReason,
    partialGroups,
    resolveMoq: resolveMoqViolations,
    resolveColli: resolveColliViolations,
  }
}

type SkuWithSettings = Parameters<typeof constraintsFromSkuSettings>[0][number]

/** {@link SkuConstraint}s for the given SKUs from `sku.getDataForPOs`; undefined until both load. */
export function useSkuConstraints(
  skuMap: ReadonlyMap<string, SkuWithSettings> | undefined,
  company: { secondaryMoqGroupVariable: string; colliSize?: number | null } | undefined | null,
  skuIds: readonly string[],
): SkuConstraint[] | undefined {
  const groupVariable = company?.secondaryMoqGroupVariable
  const companyColliSize = company?.colliSize
  return useMemo(() => {
    if (!skuMap || groupVariable === undefined) return undefined
    return constraintsFromSkuSettings(
      filterDefined(skuIds.map((id) => skuMap.get(id))),
      groupVariable,
      companyColliSize,
    )
  }, [skuMap, groupVariable, companyColliSize, skuIds])
}
